/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { CertsApiService, ServicesApiService, SIPApiService, UserInterfaceApiService } from '../../../shared/http';
import { Component, ViewChild } from '@angular/core';
import { CountDownWaitComponent } from '../../../shared/components';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LbErrorService } from '../../../shared/services/components';
import { LogService, ModalService, RbacService, UserService, UtilsService } from '../../../../../core/services';
import { UserInterfaceModel } from '../../../shared/models';
import { forkJoin, Observable } from 'rxjs';
import { portRangeValidation } from '../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-user-interface',
    templateUrl: './userInterface.component.html',
    styleUrls: ['./userInterface.component.scss']
})

export class UserInterfaceComponent extends BaseComponent {
    /**
     * Reference to the countdown container.
     * @private
     */
    @ViewChild(CountDownWaitComponent, { static: true }) private countDown: CountDownWaitComponent;

    /**
     * To store the wui form.
     */
    public serviceForm: FormGroup;

    /**
     * To store the dropdowns IPs.
     */
    public dropdownIps: DropdownModel[] = [];

    /**
     * To store the dropdown certs.
     */
    public dropdownCerts: DropdownModel[] = [];

    /**
     * To store the user interface.
     */
    public userInterface: UserInterfaceModel;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Reference to the countdown container.
     * @private
     */
    private reload: boolean = false;

    constructor(
        private certsApiService: CertsApiService,
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private modalService: ModalService,
        private rbacService: RbacService,
        private servicesAPI: ServicesApiService,
        private sipApiService: SIPApiService,
        private userInterfaceService: UserInterfaceApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpFormGroup();
        this.subscribeToFormChanges();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Logout and reload client.
     */
    public reloadAction() {
        this.userService.logout(false);
        const ipAddress = this.dropdownIps.find(item => item.key === this.serviceForm.get('ipId').value);
        window.location.href = `https://${ipAddress}:${this.serviceForm.get('apiPort').value}/`;
    }

    /**
     * Confirm submit decision.
     */
    public confirmSubmitUserInterface(): void {
        let message = '';
        const form = this.serviceForm.value;
        // If form value below have changed, inform the user they will be re-directed.
        if (
            form.ipId !== this.userInterface.ip ||
            form.apiPort !== this.userInterface.apiPort ||
            form.SSLCertificate !== this.userInterface.SSLCertificate
        ) {
            this.reload = true;
            message = '<br>You will be logged out and redirected if required';
        }

        const confirmationObject = {
            title: 'user_interface_confirm',
            message: 'Are you sure that you wish to change the user interface setting?' + message,
            callBack: this.submitUserInterface.bind(this),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Submit user interface.
     * @private
     */
    private submitUserInterface(): Observable<any> {
        return new Observable((observer): void => {
            this.userInterfaceService.putUserInterface(this.serviceForm.getRawValue())
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        if (this.reload) {
                            this.servicesAPI.restartService('httpd');
                            this.countDown.start();
                        }

                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.serviceForm, error.error.errors);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Build 'User interface' form.
     * @private
     */
    private setUpFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            ipId: [null, Validators.required],
            apiPort: [null, portRangeValidation],
            SSLCertificate: [null],
            sshEnabled: [null],
            sshPort: [null, [Validators.required, portRangeValidation]],
        });
    }

    /**
     * Subscribe to the default gateway form changes.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('serviceForm', () => {
                        if (!this.utilsService.hasFormDataChanged(this.userInterface, form)) {
                            this.serviceForm.markAsPristine();
                        } else {
                            this.serviceForm.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            sips: this.sipApiService.getStaticIPs(),
            certs: this.certsApiService.getCerts(),
            userInterface: this.userInterfaceService.getUserInterface()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.dropdownIps = this.utilsService.convertArrayToDropDown(
                        dataGroups['sips'], 'id', 'ip'
                    );
                    this.dropdownCerts = this.utilsService.convertArrayToDropDown(
                        dataGroups['certs'], 'id', 'name'
                    );
                    this.userInterface = dataGroups['userInterface'];

                    this.patchDataToForm(this.userInterface);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PhysicalAdvConfComponent setUpPageData ', error.message);
                }
            });
    }

    /**
     * Patch data to form.
     * @param userInterface
     * @private
     */
    private patchDataToForm(userInterface: UserInterfaceModel): void {
        this.serviceForm.patchValue(userInterface);
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'user-interface-set-user-interface': [{ path: '/system/userinterface', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['user-interface-set-user-interface']) {
            this.serviceForm.disable();
        }
    }
}
